import React, { useState, useEffect } from 'react';
import { graphql, useStaticQuery, Link } from 'gatsby';
import Button from './common/Button';
import BackgroundImage from 'gatsby-background-image';

interface HeroProps {
  homePageData?: {
    heroHeader?: string;
    heroSubHeader?: string;
    heroCtaButtonText?: string;
    heroCtaButtonLink?: string;
    heroImages?: Array<{
      fluid: any;
    }>;
  } | null;
}

const Hero: React.FC<HeroProps> = ({ homePageData }) => {
  const data = useStaticQuery(graphql`
    query {
      heroImage: file(relativePath: { eq: "images/hero.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1600) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      teachersCollegeImage: file(
        relativePath: { eq: "images/teachers-college-2.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1600) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      classroomBgImage: file(relativePath: { eq: "images/classroom-bg.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1600) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `);

  // Use Contentful images if available, otherwise fall back to static images
  const heroImages =
    homePageData?.heroImages && homePageData.heroImages.length > 0
      ? homePageData.heroImages.map((img: any) => img.fluid)
      : [
          data.heroImage.childImageSharp.fluid,
          data.teachersCollegeImage.childImageSharp.fluid,
          data.classroomBgImage.childImageSharp.fluid
        ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % heroImages.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <>
      <section className="py-20 magazine-bg relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content Section */}
              <div className="relative z-10">
                <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-primary mb-6 leading-tight sans-serif">
                  {homePageData?.heroHeader ||
                    'Cultivating Meaningful Learning Communities through Literacy'}
                </h1>
                <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/40 mb-8 rounded-full"></div>
                <h2 className="text-2xl font-semibold text-foreground/80 leading-relaxed mb-10 max-w-xl">
                  {homePageData?.heroSubHeader ||
                    'Research-Based, Responsive, and Inclusive Professional Development'}
                </h2>
                <Link to={homePageData?.heroCtaButtonLink || '/services'}>
                  <Button
                    className="bg-primary text-white hover:bg-primary/90 shadow-lg"
                    text={homePageData?.heroCtaButtonText || 'Learn More'}
                    size="lg"
                  />
                </Link>
              </div>

              {/* Image Carousel */}
              <div className="relative" style={{ minHeight: '500px' }}>
                {heroImages.map((image, index) => (
                  <div
                    key={index}
                    className="absolute inset-0 w-full h-full"
                    style={{
                      opacity: index === currentIndex ? 1 : 0,
                      zIndex: index === currentIndex ? 1 : 0,
                      transition: 'opacity 1s ease-in-out',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow:
                        '0 10px 40px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <BackgroundImage
                      fluid={image}
                      className="absolute inset-0 w-full h-full"
                      style={{
                        borderRadius: '12px'
                      }}
                    />
                  </div>
                ))}
                {/* Carousel indicators */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-20 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentIndex
                          ? 'bg-primary w-8'
                          : 'bg-primary/30 hover:bg-primary/50 w-2'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
